import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Users, Plus, Globe, Lock, Trophy, Calendar, ArrowRight, LogIn } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { League, LeagueMember, LeagueSeason, LeagueRound, Profile } from '@/types/game';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function LeaguesPage() {
  const navigate = useNavigate();
  const { leagueId } = useParams();
  const { user, profile } = useAuth();
  const [publicLeagues, setPublicLeagues] = useState<League[]>([]);
  const [myLeagues, setMyLeagues] = useState<League[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Create league form
  const [leagueName, setLeagueName] = useState('');
  const [leagueDescription, setLeagueDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchLeagues();
  }, [user]);

  const fetchLeagues = async () => {
    setIsLoading(true);
    try {
      // Fetch public leagues
      const { data: publicData } = await supabase
        .from('leagues')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (publicData) {
        setPublicLeagues(publicData as League[]);
      }

      // Fetch user's leagues
      if (user) {
        const { data: memberData } = await supabase
          .from('league_members')
          .select('league:leagues(*)')
          .eq('user_id', user.id);

        if (memberData) {
          setMyLeagues(memberData.map(d => d.league).filter(Boolean) as League[]);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateLeague = async () => {
    if (!user || !leagueName.trim()) {
      toast.error('Please enter a league name');
      return;
    }

    setIsCreating(true);
    try {
      const code = !isPublic ? Math.random().toString(36).substring(2, 8).toUpperCase() : null;
      
      const { data: league, error } = await supabase
        .from('leagues')
        .insert({
          name: leagueName.trim(),
          description: leagueDescription.trim() || null,
          owner_user_id: user.id,
          is_public: isPublic,
          join_code: code,
        })
        .select()
        .single();

      if (error) throw error;

      await supabase.from('league_members').insert({
        league_id: league.id,
        user_id: user.id,
        role: 'owner',
      });

      toast.success('League created!');
      setShowCreate(false);
      setLeagueName('');
      setLeagueDescription('');
      fetchLeagues();
      navigate(`/leagues/${league.id}`);
    } catch (err) {
      console.error('Error creating league:', err);
      toast.error('Failed to create league');
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinByCode = async () => {
    if (!user || !joinCode.trim()) {
      toast.error('Please enter a join code');
      return;
    }

    try {
      const { data: league, error } = await supabase
        .from('leagues')
        .select('*')
        .eq('join_code', joinCode.trim().toUpperCase())
        .single();

      if (error || !league) {
        toast.error('Invalid join code');
        return;
      }

      // Check if already a member
      const { data: existingMember } = await supabase
        .from('league_members')
        .select('user_id')
        .eq('league_id', league.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingMember) {
        toast.info('You are already a member of this league');
        navigate(`/leagues/${league.id}`);
        return;
      }

      const { error: joinError } = await supabase.from('league_members').insert({
        league_id: league.id,
        user_id: user.id,
        role: 'member',
      });

      if (joinError) {
        if (joinError.code === '23505') {
          toast.info('You are already a member of this league');
          navigate(`/leagues/${league.id}`);
          return;
        }
        throw joinError;
      }

      toast.success(`Joined ${league.name}!`);
      setJoinCode('');
      fetchLeagues();
      navigate(`/leagues/${league.id}`);
    } catch (err) {
      console.error('Error joining league:', err);
      toast.error('Failed to join league');
    }
  };

  const handleJoinPublic = async (league: League) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    try {
      // Check if already a member
      const { data: existingMember } = await supabase
        .from('league_members')
        .select('user_id')
        .eq('league_id', league.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingMember) {
        toast.info('You are already a member of this league');
        navigate(`/leagues/${league.id}`);
        return;
      }

      const { error: joinError } = await supabase.from('league_members').insert({
        league_id: league.id,
        user_id: user.id,
        role: 'member',
      });

      if (joinError) {
        if (joinError.code === '23505') {
          toast.info('You are already a member of this league');
          navigate(`/leagues/${league.id}`);
          return;
        }
        throw joinError;
      }

      toast.success(`Joined ${league.name}!`);
      fetchLeagues();
      navigate(`/leagues/${league.id}`);
    } catch (err) {
      toast.error('Failed to join league');
    }
  };

  // If viewing a specific league
  if (leagueId) {
    return <LeagueDetail leagueId={leagueId} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Users className="w-7 h-7 text-primary" />
              <h1 className="text-2xl font-semibold">Leagues</h1>
            </div>
            {user && (
              <Button onClick={() => setShowCreate(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Create League
              </Button>
            )}
          </div>

          {/* Not logged in - explanation section */}
          {!user && (
            <div className="game-card mb-8">
              <h2 className="text-lg font-medium mb-3">Compete with friends</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Leagues are weekly competitions where you and others race through the same Wikipedia routes. 
                Create a private league for your friends, or join a public one to compete with players worldwide.
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <Trophy className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>New challenges each week with leaderboards</span>
                </li>
                <li className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>Create private leagues with invite codes</span>
                </li>
                <li className="flex items-start gap-2">
                  <Globe className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>Join public leagues to find new competitors</span>
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/auth">
                  <Button className="gap-2 w-full sm:w-auto">
                    <LogIn className="w-4 h-4" />
                    Sign up to create a league
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="outline" className="gap-2 w-full sm:w-auto">
                    Log in to join
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Create League Modal */}
          {showCreate && (
            <div className="game-card mb-8">
              <h2 className="text-lg font-medium mb-4">Create New League</h2>
              <div className="space-y-4">
                <div>
                  <Label>League Name</Label>
                  <Input
                    value={leagueName}
                    onChange={(e) => setLeagueName(e.target.value)}
                    placeholder="My Awesome League"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={leagueDescription}
                    onChange={(e) => setLeagueDescription(e.target.value)}
                    placeholder="What's this league about?"
                    rows={3}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={isPublic} onCheckedChange={setIsPublic} />
                  <Label>Public (anyone can join)</Label>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCreateLeague} disabled={isCreating}>
                    {isCreating ? 'Creating...' : 'Create'}
                  </Button>
                  <Button variant="ghost" onClick={() => setShowCreate(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Join by Code */}
          {user && (
            <div className="game-card mb-8">
              <h2 className="text-base font-medium mb-3">Join Private League</h2>
              <div className="flex gap-2">
                <Input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  placeholder="Enter join code"
                  className="max-w-xs"
                />
                <Button onClick={handleJoinByCode} variant="outline">
                  Join
                </Button>
              </div>
            </div>
          )}

          <Tabs defaultValue={user ? 'my' : 'public'}>
            <TabsList className="mb-6">
              {user && <TabsTrigger value="my">My Leagues</TabsTrigger>}
              <TabsTrigger value="public">Public Leagues</TabsTrigger>
            </TabsList>

            {user && (
              <TabsContent value="my">
                {myLeagues.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    You haven't joined any leagues yet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myLeagues.map((league) => (
                      <LeagueCard
                        key={league.id}
                        league={league}
                        onClick={() => navigate(`/leagues/${league.id}`)}
                        isMember
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            <TabsContent value="public">
              {publicLeagues.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No public leagues available.
                </div>
              ) : (
                <div className="space-y-3">
                  {publicLeagues.map((league) => {
                    const isMember = myLeagues.some(l => l.id === league.id);
                    return (
                      <LeagueCard
                        key={league.id}
                        league={league}
                        onClick={() => isMember ? navigate(`/leagues/${league.id}`) : handleJoinPublic(league)}
                        isMember={isMember}
                      />
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

function LeagueCard({ league, onClick, isMember }: { league: League; onClick: () => void; isMember: boolean }) {
  return (
    <div
      className="game-card cursor-pointer hover:border-primary/40 transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-full bg-primary/15">
          <Users className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{league.name}</h3>
            {league.is_public ? (
              <Globe className="w-4 h-4 text-muted-foreground" />
            ) : (
              <Lock className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
          {league.description && (
            <p className="text-sm text-muted-foreground line-clamp-1">{league.description}</p>
          )}
        </div>
        <Button variant={isMember ? 'secondary' : 'default'} size="sm">
          {isMember ? 'View' : 'Join'}
        </Button>
      </div>
    </div>
  );
}

function LeagueDetail({ leagueId }: { leagueId: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [league, setLeague] = useState<League | null>(null);
  const [members, setMembers] = useState<(LeagueMember & { profile?: Profile })[]>([]);
  const [seasons, setSeasons] = useState<LeagueSeason[]>([]);
  const [rounds, setRounds] = useState<LeagueRound[]>([]);
  const [isMember, setIsMember] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLeagueData();
  }, [leagueId, user]);

  const fetchLeagueData = async () => {
    setIsLoading(true);
    try {
      // Fetch league
      const { data: leagueData } = await supabase
        .from('leagues')
        .select('*')
        .eq('id', leagueId)
        .single();

      if (!leagueData) {
        navigate('/leagues');
        return;
      }
      setLeague(leagueData as League);

      // Fetch members
      const { data: membersData } = await supabase
        .from('league_members')
        .select('*, profile:profiles(*)')
        .eq('league_id', leagueId);

      if (membersData) {
        setMembers(membersData as (LeagueMember & { profile?: Profile })[]);
        setIsMember(user ? membersData.some(m => m.user_id === user.id) : false);
      }

      // Fetch seasons
      const { data: seasonsData } = await supabase
        .from('league_seasons')
        .select('*')
        .eq('league_id', leagueId)
        .order('starts_at', { ascending: false });

      if (seasonsData) {
        setSeasons(seasonsData as LeagueSeason[]);
        
        // Fetch rounds for active season
        const activeSeason = seasonsData.find(s => s.status === 'active');
        if (activeSeason) {
          const { data: roundsData } = await supabase
            .from('league_rounds')
            .select('*, match:matches(*)')
            .eq('season_id', activeSeason.id)
            .order('round_number', { ascending: true });

          if (roundsData) {
            setRounds(roundsData as LeagueRound[]);
          }
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeave = async () => {
    if (!user || !league) return;

    try {
      await supabase
        .from('league_members')
        .delete()
        .eq('league_id', league.id)
        .eq('user_id', user.id);

      toast.success('Left league');
      navigate('/leagues');
    } catch (err) {
      toast.error('Failed to leave league');
    }
  };

  const handleJoin = async () => {
    if (!user || !league) {
      navigate('/auth');
      return;
    }

    // Check if already a member (defensive check)
    if (isMember) {
      toast.info('You are already a member of this league');
      return;
    }

    try {
      const { error: joinError } = await supabase.from('league_members').insert({
        league_id: league.id,
        user_id: user.id,
        role: 'member',
      });

      if (joinError) {
        if (joinError.code === '23505') {
          toast.info('You are already a member of this league');
          fetchLeagueData();
          return;
        }
        throw joinError;
      }

      toast.success('Joined league!');
      fetchLeagueData();
    } catch (err) {
      toast.error('Failed to join league');
    }
  };

  if (isLoading || !league) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground">Loading league...</p>
        </div>
      </div>
    );
  }

  const activeSeason = seasons.find(s => s.status === 'active');
  const isOwner = user && league.owner_user_id === user.id;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* League Header */}
          <div className="game-card mb-8">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="p-4 rounded-full bg-primary/15">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-semibold">{league.name}</h1>
                    {league.is_public ? (
                      <Globe className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <Lock className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  {league.description && (
                    <p className="text-muted-foreground mt-1">{league.description}</p>
                  )}
                </div>
              </div>
              {!isMember && league.is_public && (
                <Button onClick={handleJoin}>Join League</Button>
              )}
            </div>

            {/* Join Code for private leagues */}
            {!league.is_public && league.join_code && isOwner && (
              <div className="bg-secondary/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-muted-foreground mb-1">Invite Code</p>
                <p className="font-mono text-lg">{league.join_code}</p>
              </div>
            )}

            {/* Members count */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{members.length} member{members.length !== 1 ? 's' : ''}</span>
              {activeSeason && (
                <span>Season active</span>
              )}
            </div>
          </div>

          {/* Season Info */}
          {activeSeason && rounds.length > 0 && (
            <div className="game-card mb-8">
              <h2 className="text-lg font-medium mb-4">Current Season</h2>
              <div className="space-y-3">
                {rounds.map((round) => {
                  const isActive = new Date() >= new Date(round.starts_at) && new Date() <= new Date(round.ends_at);
                  const isPast = new Date() > new Date(round.ends_at);
                  
                  return (
                    <div
                      key={round.id}
                      className={`p-4 rounded-lg border ${
                        isActive ? 'border-primary bg-primary/5' : 'border-border/60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Round {round.round_number}</p>
                          {round.match && (
                            <p className="text-sm text-muted-foreground">
                              {round.match.start_title} → {round.match.target_title}
                            </p>
                          )}
                        </div>
                        {isActive && round.match && (
                          <Button
                            size="sm"
                            onClick={() => navigate(`/play?matchId=${round.match!.id}`)}
                          >
                            Play
                          </Button>
                        )}
                        {isPast && (
                          <span className="text-xs text-muted-foreground">Ended</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Members */}
          <div className="game-card mb-8">
            <h2 className="text-lg font-medium mb-4">Members</h2>
            <div className="space-y-2">
              {members.map((member) => (
                <div key={member.user_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {(member.profile?.display_name || member.profile?.username || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium">
                      {member.profile?.display_name || member.profile?.username}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground capitalize">{member.role}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Leave button */}
          {isMember && !isOwner && (
            <Button variant="outline" onClick={handleLeave}>
              Leave League
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
